import { useEffect, useRef, useState } from 'react';
import { callApi } from '../lib/api.ts';
import type { Client } from '../lib/types.ts';
import { friendlyError } from '../lib/friendlyError.ts';
import { sectionDesc } from '../lib/ui.ts';

/**
 * Assets step (assets-step.md §4/§5) — upload/list/delete the client's static site-chrome files
 * (SVG/PNG logos, maps, graphics) committed to sites/<slug>/public/assets/ on app/<slug>. The repo
 * IS the catalogue (no DB table) — this reads/writes via the `assets` endpoint.
 *
 * Previews are INSTANT (no CF-build lag): the list action returns each asset's committed content —
 * SVG as sanitized text (rendered inline; the server re-sanitizes before returning it), raster as a
 * data: URI. Over the size bound it returns preview:null → we fall back to the CF branch-alias URL
 * (may lag a build) / an ext chip. And the moment a file is PICKED we show a local
 * URL.createObjectURL preview, before the upload even finishes.
 */
const ACCEPT = '.svg,.png,.jpg,.jpeg,.webp,.gif,.ico,image/svg+xml,image/png,image/jpeg,image/webp,image/gif,image/x-icon';

type Asset = { name: string; path: string; kind: 'svg' | 'raster'; preview: string | null };
type Pending = { name: string; url: string };

/** File → bare base64 (strip the data: URL prefix; the endpoint tolerates either). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error ?? new Error('read failed'));
    r.readAsDataURL(file);
  });
}

/** A fixed thumbnail box. Inline SVG (sanitized) scales to fit; raster/URL via <img>; else a chip. */
function Thumb({ asset, previewBase }: { asset: Asset; previewBase: string | null }) {
  const box = 'h-10 w-10 flex-none rounded border border-line bg-paper';
  if (asset.kind === 'svg' && asset.preview) {
    return (
      <span
        className={`${box} flex items-center justify-center overflow-hidden p-0.5 [&>svg]:max-h-full [&>svg]:max-w-full`}
        // Safe: the server sanitizes the SVG on upload AND re-sanitizes it on read before returning.
        dangerouslySetInnerHTML={{ __html: asset.preview }}
      />
    );
  }
  const src = asset.preview ?? (previewBase ? `${previewBase}/assets/${encodeURIComponent(asset.name)}` : null);
  if (src) return <img src={src} alt="" className={`${box} object-contain`} loading="lazy" />;
  return (
    <span className={`${box} flex items-center justify-center text-[10px] text-ink-2`}>
      {asset.name.split('.').pop()?.toUpperCase()}
    </span>
  );
}

export function AssetsUploader({ client, onIngested, canMaintainLive = false }: { client: Client; onIngested: () => void; canMaintainLive?: boolean }) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [previewBase, setPreviewBase] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const objectUrls = useRef<string[]>([]);
  // go-live-protection §3a: disabled only when live AND not the admin maintenance carve-out.
  const liveBlocked = client.is_live && !canMaintainLive;

  function clearPending() {
    objectUrls.current.forEach((u) => URL.revokeObjectURL(u));
    objectUrls.current = [];
    setPending([]);
  }
  useEffect(() => () => clearPending(), []); // revoke any object URLs on unmount

  async function refresh() {
    try {
      const r = await callApi<{ assets: Asset[]; previewBase: string | null }>('assets', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, action: 'list' }),
      });
      setAssets(r.assets);
      setPreviewBase(r.previewBase);
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  async function onFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const picked = Array.from(fileList);
    // Instant local preview the moment files are picked (before the upload finishes).
    const previews = picked.map((f) => ({ name: f.name, url: URL.createObjectURL(f) }));
    objectUrls.current.push(...previews.map((p) => p.url));
    setPending(previews);
    setBusy(true);
    setErr(null);
    try {
      const files = await Promise.all(picked.map(async (f) => ({ filename: f.name, dataBase64: await fileToBase64(f) })));
      await callApi('assets', { method: 'POST', body: JSON.stringify({ clientId: client.id, action: 'upload', files }) });
      if (inputRef.current) inputRef.current.value = ''; // let the same file be re-picked
      await refresh();
      onIngested();
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      clearPending();
      setBusy(false);
    }
  }

  async function remove(name: string) {
    setBusy(true);
    setErr(null);
    try {
      await callApi('assets', { method: 'POST', body: JSON.stringify({ clientId: client.id, action: 'delete', name }) });
      await refresh();
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  const empty = !loading && assets.length === 0 && pending.length === 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          disabled={liveBlocked || busy}
          onChange={(e) => void onFiles(e.target.files)}
          className="text-sm"
        />
        {busy && <span className="text-xs text-ink-2">Working…</span>}
      </div>
      <p className={sectionDesc}>
        SVG, PNG, JPG, WEBP, GIF or ICO · up to 2 MB each. SVGs are sanitised on upload so they’re safe
        to inline &amp; animate.
      </p>
      {err && <p className="text-sm text-danger">{err}</p>}

      {loading ? (
        <p className="text-xs text-ink-2">Loading…</p>
      ) : empty ? (
        <p className="text-xs text-ink-2">No assets yet.</p>
      ) : (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {/* Pending (just-picked) previews first — instant, dimmed while uploading. */}
          {pending.map((p) => (
            <li key={`pending-${p.url}`} className="flex items-center gap-2 rounded border border-line bg-paper-2 p-2 opacity-60">
              <img src={p.url} alt="" className="h-10 w-10 flex-none rounded border border-line bg-paper object-contain" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-mono text-xs text-ink-2" title={p.name}>{p.name}</div>
                <div className="truncate text-[10px] text-ink-2">Uploading…</div>
              </div>
            </li>
          ))}
          {assets.map((a) => (
            <li key={a.path} className="flex items-center gap-2 rounded border border-line bg-paper-2 p-2">
              <Thumb asset={a} previewBase={previewBase} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-mono text-xs text-ink-2" title={a.name}>{a.name}</div>
                <div className="truncate text-[10px] text-ink-2">/assets/{a.name}</div>
              </div>
              <button
                onClick={() => void remove(a.name)}
                disabled={liveBlocked || busy}
                className="flex-none rounded border border-line px-1.5 text-xs text-ink-2 hover:bg-danger-bright/10 hover:text-danger disabled:opacity-30"
                title="Delete asset"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
